import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, User, Search, ArrowUpDown, ArrowRight, ArrowLeft, ArrowLeftRight, Users, RotateCcw } from 'lucide-react';
import Pagination from './Pagination';
import type { Player, Team, GenderFilter, SideFilter } from '../types';
import { formatCurrency } from '../utils/helpers';

interface SoldPlayersListProps {
  players: Player[];
  teams: Team[];
}

type SortField = 'name' | 'gender' | 'side' | 'price' | 'team';
type SortOrder = 'asc' | 'desc';

interface FilterCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  totalCount?: number;
  active: boolean;
  onClick: () => void;
  color?: 'indigo' | 'amber' | 'pink' | 'blue' | 'green' | 'purple';
}

const FilterCard = ({ icon, label, count, totalCount, active, onClick, color = 'indigo' }: FilterCardProps) => {
  const colorStyles = {
    indigo: 'bg-indigo-100 text-indigo-600',
    amber: 'bg-amber-100 text-amber-600',
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
        active
          ? 'border-indigo-500 bg-white shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
        {icon}
      </div>
      <div className="text-left">
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800">
          {count}
          {totalCount !== undefined && totalCount !== count && (
            <span className="text-sm text-slate-400 font-normal ml-1">/ {totalCount}</span>
          )}
        </p>
      </div>
    </button>
  );
};

const SoldPlayersList = ({ players, teams }: SoldPlayersListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');
  const [sortField, setSortField] = useState<SortField>('price');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  const soldPlayers = useMemo(() => players.filter(p => p.status === 'sold'), [players]);

  const teamMap = useMemo(() => {
    const map = new Map<string, Team>();
    teams.forEach(team => map.set(team.id, team));
    return map;
  }, [teams]);

  // Totals for stat cards
  const totalCounts = useMemo(() => {
    const male = soldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length;
    const female = soldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length;
    const right = soldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length;
    const left = soldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length;
    const both = soldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length;
    return { total: soldPlayers.length, male, female, right, left, both };
  }, [soldPlayers]);

  // Counts that respect active filters (matches Players view logic)
  const dynamicCounts = useMemo(() => {
    const sideFiltered = sideFilter === 'all'
      ? soldPlayers
      : soldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === sideFilter);

    const genderFiltered = genderFilter === 'all'
      ? soldPlayers
      : soldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === genderFilter);

    return {
      total: soldPlayers.length,
      male: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length,
      female: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length,
      right: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length,
      left: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length,
      both: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length,
    };
  }, [soldPlayers, genderFilter, sideFilter]);

  const filteredPlayers = useMemo(() => {
    return soldPlayers.filter(p => {
      if (genderFilter !== 'all') {
        const gender = p.details?.player_gender?.toLowerCase();
        if (gender !== genderFilter) return false;
      }
      if (sideFilter !== 'all') {
        const side = p.details?.playing_side?.toLowerCase();
        if (side !== sideFilter) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const teamName = p.teamId ? teamMap.get(p.teamId)?.name.toLowerCase() : '';
        const jerseyNumber = p.details?.jersey_number ? String(p.details.jersey_number).toLowerCase() : '';
        return (
          p.name.toLowerCase().includes(q) ||
          p.mobile_number?.includes(q) ||
          p.details?.player_full_name?.toLowerCase().includes(q) ||
          p.details?.jersey_name?.toLowerCase().includes(q) ||
          (jerseyNumber && jerseyNumber.includes(q)) ||
          (teamName && teamName.includes(q))
        );
      }
      return true;
    });
  }, [soldPlayers, genderFilter, sideFilter, searchQuery, teamMap]);

  const sortedPlayers = useMemo(() => {
    const result = [...filteredPlayers];

    result.sort((a, b) => {
      switch (sortField) {
        case 'price':
          return sortOrder === 'asc' ? a.soldPrice - b.soldPrice : b.soldPrice - a.soldPrice;
        case 'team': {
          const aTeam = (a.teamId && teamMap.get(a.teamId)?.name?.toLowerCase()) || 'zzz';
          const bTeam = (b.teamId && teamMap.get(b.teamId)?.name?.toLowerCase()) || 'zzz';
          return sortOrder === 'asc' ? aTeam.localeCompare(bTeam) : bTeam.localeCompare(aTeam);
        }
        case 'gender': {
          const aGender = a.details?.player_gender?.toLowerCase() || 'z';
          const bGender = b.details?.player_gender?.toLowerCase() || 'z';
          return sortOrder === 'asc' ? aGender.localeCompare(bGender) : bGender.localeCompare(aGender);
        }
        case 'side': {
          const aSide = a.details?.playing_side?.toLowerCase() || 'z';
          const bSide = b.details?.playing_side?.toLowerCase() || 'z';
          return sortOrder === 'asc' ? aSide.localeCompare(bSide) : bSide.localeCompare(aSide);
        }
        case 'name':
        default: {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          return sortOrder === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
        }
      }
    });

    return result;
  }, [filteredPlayers, sortField, sortOrder, teamMap]);

  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedPlayers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, genderFilter, sideFilter]);

  const resetFilters = () => {
    setGenderFilter('all');
    setSideFilter('all');
    setSearchQuery('');
  };

  const isFiltersActive = genderFilter === 'all' && sideFilter === 'all';

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'price' ? 'desc' : 'asc');
    }
  };

  const getSideIcon = (side?: string) => {
    switch (side?.toLowerCase()) {
      case 'right': return <ArrowRight className="w-4 h-4" />;
      case 'left': return <ArrowLeft className="w-4 h-4" />;
      case 'both': return <ArrowLeftRight className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Sold Players</h2>
              <p className="text-white/80">{soldPlayers.length} players acquired by teams</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">{filteredPlayers.length}</p>
            <p className="text-white/70 text-sm">Showing</p>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {(!isFiltersActive || searchQuery) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">Active filters:</span>
          {genderFilter !== 'all' && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
              {genderFilter === 'male' ? 'Male' : 'Female'}
            </span>
          )}
          {sideFilter !== 'all' && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full capitalize">
              {sideFilter} Side
            </span>
          )}
          {searchQuery && (
            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
              "{searchQuery}"
            </span>
          )}
          <span className="text-sm font-medium text-indigo-600 ml-2">
            Showing {filteredPlayers.length} of {soldPlayers.length} sold players
          </span>
          <button
            onClick={resetFilters}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-sm text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Reset All
          </button>
        </div>
      )}

      {/* Filter Cards - Gender */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="All Sold"
          count={filteredPlayers.length}
          totalCount={totalCounts.total}
          active={isFiltersActive}
          onClick={resetFilters}
          color="indigo"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Male Players"
          count={dynamicCounts.male}
          totalCount={totalCounts.male}
          active={genderFilter === 'male'}
          onClick={() => setGenderFilter(genderFilter === 'male' ? 'all' : 'male')}
          color="amber"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Female Players"
          count={dynamicCounts.female}
          totalCount={totalCounts.female}
          active={genderFilter === 'female'}
          onClick={() => setGenderFilter(genderFilter === 'female' ? 'all' : 'female')}
          color="pink"
        />
      </div>

      {/* Filter Cards - Playing Side */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<ArrowRight className="w-5 h-5" />}
          label="Right Side"
          count={dynamicCounts.right}
          totalCount={totalCounts.right}
          active={sideFilter === 'right'}
          onClick={() => setSideFilter(sideFilter === 'right' ? 'all' : 'right')}
          color="blue"
        />
        <FilterCard
          icon={<ArrowLeft className="w-5 h-5" />}
          label="Left Side"
          count={dynamicCounts.left}
          totalCount={totalCounts.left}
          active={sideFilter === 'left'}
          onClick={() => setSideFilter(sideFilter === 'left' ? 'all' : 'left')}
          color="green"
        />
        <FilterCard
          icon={<ArrowLeftRight className="w-5 h-5" />}
          label="Both Sides"
          count={dynamicCounts.both}
          totalCount={totalCounts.both}
          active={sideFilter === 'both'}
          onClick={() => setSideFilter(sideFilter === 'both' ? 'all' : 'both')}
          color="purple"
        />
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, team, mobile, jersey, or full name..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Players Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">#</th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => toggleSort('name')}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase hover:text-slate-700"
                  >
                    Player <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleSort('gender')}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase hover:text-slate-700"
                  >
                    Gender <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleSort('side')}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase hover:text-slate-700"
                  >
                    Side <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => toggleSort('team')}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase hover:text-slate-700"
                  >
                    Team <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right">
                  <button
                    onClick={() => toggleSort('price')}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase hover:text-slate-700"
                  >
                    Price <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Jersey</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedPlayers.map((player, index) => {
                const team = player.teamId ? teamMap.get(player.teamId) : null;
                return (
                  <tr key={player.id} className="hover:bg-emerald-50 transition-colors">
                    <td className="px-4 py-3 text-slate-400 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {player.details?.photo_url ? (
                          <img
                            src={player.details.photo_url}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-800">{player.name}</p>
                          <p className="text-xs text-slate-500">{player.mobile_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        player.details?.player_gender?.toLowerCase() === 'male'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-pink-100 text-pink-700'
                      }`}>
                        {player.details?.player_gender || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                        {getSideIcon(player.details?.playing_side)}
                        {player.details?.playing_side || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-semibold text-slate-800">{team?.name || '-'}</p>
                        <p className="text-xs text-slate-500">{team?.ownerName || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                      {formatCurrency(player.soldPrice)} pts
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-slate-600 text-sm">
                        {player.details?.jersey_name || '-'}
                        {player.details?.jersey_number && ` #${player.details.jersey_number}`}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filteredPlayers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No sold players found</p>
                    <p className="text-sm">Adjust your filters or search</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredPlayers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredPlayers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default SoldPlayersList;
